// src/pages/Drivers.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Chip,
  Stack,
  Button,
  TableContainer,
} from '@mui/material';
import useDrivers from '../hooks/useDrivers';
import useVehicles from '../hooks/useVehicles'

// Highlight search matches
function highlightMatch(text, query) {
  if (!text) return '-';
  if (!query.trim()) return text;

  const str = String(text);
  const lowerStr = str.toLowerCase();
  const lowerQuery = query.toLowerCase();

  const index = lowerStr.indexOf(lowerQuery);
  if (index === -1) return str;

  const before = str.slice(0, index);
  const match = str.slice(index, index + query.length);
  const after = str.slice(index + query.length);

  return (
    <>
      {before}
      <span
        style={{
          backgroundColor: 'rgba(255, 245, 157, 0.9)',
          borderRadius: 3,
          padding: '0 2px',
        }}
      >
        {match}
      </span>
      {after}
    </>
  );
}

// Build a short single-line address
function formatAddress(address) {
  if (!address) return '-';
  const parts = [
    address.line_1,
    address.town,
    address.postal_code,
  ].filter(Boolean);
  return parts.join(', ') || '-';
}

export default function Drivers() {
    const navigate = useNavigate()
    const { drivers, loading } = useDrivers();
    const {vehicles } = useVehicles()

  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Filter by name, email, phone, postcode
  const filteredDrivers = React.useMemo(() => {
    if (!search.trim()) return drivers;

    const q = search.trim().toLowerCase();

    return drivers.filter((d) => {
      const fullName = `${d.firstname || ''} ${d.lastname || ''}`.trim();
      const email = d.email || '';
      const phone = d.phone || '';
      const postcode = d?.address?.postal_code || '';

      return (
        fullName.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q) ||
        phone.toLowerCase().includes(q) ||
        postcode.toLowerCase().includes(q)
      );
    });
  }, [drivers, search]);

  // Reset page on search change
  React.useEffect(() => {
    setPage(0);
  }, [search]);

  // Pagination slice
  const paginatedDrivers = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredDrivers.slice(start, start + rowsPerPage);
  }, [filteredDrivers, page, rowsPerPage]);

    const vehicleById = React.useMemo(() => {
    const map = {};
    vehicles.forEach((v) => {
        if (v.id) map[v.id] = v;
    });
    return map;
    }, [vehicles])

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Drivers
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        View and search your drivers. Vehicles and tests are managed elsewhere.
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            p: 2,
          }}
        >
          {/* Header: search + stats */}
        <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' },
            mb: 2,
        }}
        >
        <TextField
            label="Search drivers…"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 320 }}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Button
            variant="contained"
            onClick={() => navigate('/drivers/new')}
            sx={{ textTransform: 'none' }}
        >
            Add Driver
        </Button>
        </Box>


          {/* Table */}
          <TableContainer sx={{overflowX: 'scroll'}}>
            <Table size="small">
                <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vehicle ID</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {paginatedDrivers.length === 0 && (
                    <TableRow>
                    <TableCell colSpan={5}>
                        <Typography variant="body2" sx={{ py: 2 }}>
                        No drivers match this search.
                        </Typography>
                    </TableCell>
                    </TableRow>
                )}

                {paginatedDrivers.map((d) => {
                    const fullName = `${d.firstname || ''} ${d.lastname || ''}`.trim() || '-';
                    const email = d.email || '-';
                    const phone = d.phone || '-';
                    const addressLine = formatAddress(d.address);
                    const postcode = d?.address?.postal_code || '';
                    const role = d.role || 'DRIVER';
                    const vehicleId = d.vehicle || '-';

                    return (
                    <TableRow
                        key={d._id || `${d.email}-${d.phone}`}
                        hover
                        sx={{
                        '&:nth-of-type(odd)': {
                            backgroundColor: 'rgba(249,250,251,0.9)',
                        },
                        }}
                    >
                        {/* Name */}
                        <TableCell>
                        <Typography
                            sx={{
                            fontWeight: 600,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': { color: 'primary.main' },
                            }}
                            onClick={() => navigate(`/drivers/${d._id}`)}
                        >
                            {d.firstname} {d.lastname}
                        </Typography>
                        </TableCell>


                        {/* Contact */}
                        <TableCell>
                        <Typography variant="body2">
                            {highlightMatch(email, search)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {highlightMatch(phone, search)}
                        </Typography>
                        </TableCell>

                        {/* Address */}
                        <TableCell>
                        <Typography variant="body2">
                            {highlightMatch(addressLine, search)}
                        </Typography>
                        {postcode && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {highlightMatch(postcode, search)}
                            </Typography>
                        )}
                        </TableCell>

                        {/* Role */}
                        <TableCell>
                        <Chip
                            size="small"
                            label={role}
                            color={role === 'ADMIN' ? 'secondary' : 'default'}
                            variant={role === 'ADMIN' ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 500 }}
                        />
                        </TableCell>

                        {/* Vehicle ID (for now just raw ID; could later map to VRM) */}
                        <TableCell>
                        {vehicleId && vehicleById[vehicleId] ? (
                            <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                cursor: 'pointer',
                                color: 'primary.main',
                                textDecoration: 'underline',
                            }}
                            onClick={() => navigate(`/vehicles/${vehicleById[vehicleId].id}`)}
                            >
                            {vehicleById[vehicleId].vrm}
                            </Typography>
                        ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Unassigned
                            </Typography>
                        )}
                        </TableCell>
                    </TableRow>
                    );
                })}
                </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredDrivers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
}
